import type { CausalSeamView, ReplayRailNode } from "@exploitlens/ui-contracts";

import { useSelection } from "../../context/SelectionContext";
import { formatRelation } from "../../lib/format";
import { EffectLedger } from "./EffectLedger";

function SeamNodeCard({
  label,
  node,
  side,
  emptyLabel,
}: {
  label: string;
  node: ReplayRailNode | undefined;
  side: "vulnerable" | "patched";
  emptyLabel: string;
}) {
  const { selection, select } = useSelection();
  if (!node) {
    return (
      <div className="flex-1 rounded border border-dashed border-rule-strong p-4">
        <p className="text-xs uppercase tracking-wide text-ink-faint">{label}</p>
        <p className="mt-2 text-sm text-ink-muted">{emptyLabel}</p>
      </div>
    );
  }

  const isSelected =
    (selection?.side === side && selection.nodeId === node.id) ||
    (selection?.side !== side && selection?.counterpartId === node.id);

  return (
    <button
      type="button"
      onClick={() => select({ side, nodeId: node.id, counterpartId: node.counterpartId })}
      className={[
        "flex-1 rounded border p-4 text-left",
        isSelected
          ? "border-accent bg-canvas"
          : "border-rule-strong bg-canvas hover:bg-canvas-raised",
      ].join(" ")}
    >
      <p className="text-xs uppercase tracking-wide text-ink-faint">{label}</p>
      <p className="mt-2 font-evidence text-sm text-ink">{node.label}</p>
      <p className="mt-1 truncate text-xs text-ink-muted" title={node.semanticPath}>
        {node.semanticPath}
      </p>
    </button>
  );
}

export function CausalSeam({ seam }: { seam: CausalSeamView }) {
  return (
    <section
      aria-labelledby="causal-seam-heading"
      className="rounded-lg border-2 border-rule-strong bg-canvas-raised p-6 sm:p-8"
    >
      <div className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 id="causal-seam-heading" className="font-editorial text-2xl text-ink">
          Causal Seam
        </h2>
        <span
          className={[
            "rounded-full border px-3 py-1 text-xs font-medium",
            seam.status === "found"
              ? "border-accent text-accent"
              : "border-rule-strong border-dashed text-ink-muted",
          ].join(" ")}
        >
          {seam.status === "found" ? "First divergence identified" : "Inconclusive"}
        </span>
      </div>

      {seam.status === "found" ? (
        <div className="mt-6 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
          <SeamNodeCard
            label="Vulnerable"
            node={seam.baselineNode}
            side="vulnerable"
            emptyLabel="No baseline counterpart — this call was inserted on the patched side."
          />
          <div className="flex shrink-0 flex-col items-center gap-1 px-2 text-ink-faint">
            <span aria-hidden="true">→</span>
            {seam.relation && (
              <span className="text-xs text-ink-muted">{formatRelation(seam.relation)}</span>
            )}
          </div>
          <SeamNodeCard
            label="Patched"
            node={seam.patchedNode}
            side="patched"
            emptyLabel="No patched counterpart — this call was removed."
          />
        </div>
      ) : (
        <div className="mt-6 rounded border border-dashed border-rule-strong bg-canvas p-5">
          <p className="text-sm text-ink">
            The evidence did not attribute the missing prohibited effect to a single causal call
            node.
          </p>
          {seam.reasons.length > 0 && (
            <ul className="mt-3 list-inside list-disc space-y-1 text-sm text-ink-muted">
              {seam.reasons.map((reason) => (
                <li key={reason}>{reason}</li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
        <EffectLedger
          title="Effects explained on the vulnerable side"
          effects={seam.explainedEffects}
        />
        <EffectLedger
          title="Effects observed at the patched counterpart"
          effects={seam.patchedEffects}
        />
      </div>
    </section>
  );
}
